/*
 * Mery – Produktseite (<mery-product>): Varianten (Format-Karten, Farb-Swatches), Galerie (Swipe, Thumbnails, Zoom),
 * Personalisierung nur bei passenden Formaten, „Passend dazu“ gemeinsam in den Warenkorb,
 * Sticky-Leiste mobil, „Zuletzt angesehen“ speichern.
 * Warenkorb: /cart/add.js (inkl. Foto-Upload), danach Horizon-Event, damit Warenkorb-Drawer & Zähler aktualisieren.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const money = (cents) => {
  const format = window.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: format }).format(cents / 100);
};

class MeryProduct extends HTMLElement {
  connectedCallback() {
    this.product = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{}');
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.personalKeywords = (this.dataset.personalKeywords || '')
      .split(',')
      .map((word) => word.trim().toLowerCase())
      .filter(Boolean);

    this.form?.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-position]')) this.onOptionChange();
      if (event.target.matches('[data-selling-plan]')) this.updatePrice();
    });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));

    this.querySelector('[data-personalize-link]')?.addEventListener('click', () => {
      const personalFormat = Array.from(this.querySelectorAll('.mery-format-cards__input')).find((input) =>
        this.isPersonal(input.value)
      );
      if (personalFormat && !personalFormat.checked) {
        personalFormat.checked = true;
        this.onOptionChange();
      }
    });

    this.initTiers();
    this.initGallery();
    this.initSticky();
    this.onOptionChange(false);
    this.rememberView();
  }

  /* ---------- Varianten ---------- */

  get selectedOptions() {
    const options = [];
    this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
      options[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    return options;
  }

  findVariant(options) {
    // Produkte ohne Auswahl (eine Variante): aktuelle Variante aus dem Formular nehmen
    if (!this.querySelector('[data-option-position]')) {
      const id = Number(this.querySelector('[data-variant-id]')?.value);
      return this.product.variants?.find((variant) => variant.id === id) || this.product.variants?.[0];
    }
    return this.product.variants?.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  /* ---------- Mengenstaffel (gedruckte Karten) ---------- */

  // Zeigt live „60 Karten × 5,80 € = 348,00 €“ und markiert die passende Staffel.
  initTiers() {
    this.qtyInput = this.querySelector('[data-quantity]');
    this.tierRows = Array.from(this.querySelectorAll('[data-tier-min]'));
    this.qtyTotal = this.querySelector('[data-qty-total]');
    if (!this.qtyInput || !this.tierRows.length || !this.qtyTotal) return;
    this.qtyInput.addEventListener('input', () => this.updateTiers());
    this.updateTiers();
  }

  updateTiers() {
    const qty = Math.floor(Number(this.qtyInput.value) || 0);
    const min = Number(this.qtyInput.min) || 1;
    let active = null;
    this.tierRows.forEach((row) => {
      if (qty >= Number(row.dataset.tierMin)) active = row;
    });
    this.tierRows.forEach((row) => row.classList.toggle('is-active', row === active));
    if (!active || qty < min) {
      this.qtyTotal.textContent = window.MeryStrings.minQuantity?.replace('[min]', min) || '';
      this.qtyTotal.classList.add('is-error');
      return;
    }
    const per = Number(active.dataset.tierPrice);
    this.qtyTotal.classList.remove('is-error');
    this.qtyTotal.textContent = this.qtyTotal.dataset.template
      .replace('[qty]', qty)
      .replace('[per]', money(per))
      .replace('[total]', money(per * qty));
  }

  /* ---------- Abo (Selling Plans) ---------- */

  // Zeigt nur die Abo-Pläne der gewählten Variante. Varianten ohne Plan (z. B. vorausbezahlte Pakete) = einmaliger Kauf.
  updatePurchase() {
    const fieldset = this.querySelector('[data-purchase]');
    if (!fieldset) return;
    const planIds = (this.variant?.selling_plan_allocations || []).map((allocation) => String(allocation.selling_plan_id));
    const subscriptionOnly = this.hasAttribute('data-subscription-only');
    const oneTime = fieldset.querySelector('[data-one-time] input');
    const plans = Array.from(fieldset.querySelectorAll('[data-plan-item]'));

    plans.forEach((item) => {
      const input = item.querySelector('input');
      const allowed = planIds.includes(input.value);
      item.hidden = !allowed;
      input.disabled = !allowed;
      if (!allowed) input.checked = false;
    });

    if (!planIds.length) {
      fieldset.hidden = true;
      if (oneTime) oneTime.checked = true;
      return;
    }

    if (oneTime) oneTime.closest('[data-one-time]').hidden = oneTime.disabled = subscriptionOnly;
    const checked = fieldset.querySelector('[data-selling-plan]:checked:not(:disabled)');
    if (!checked) {
      const first = subscriptionOnly || !oneTime ? plans.find((item) => !item.hidden)?.querySelector('input') : oneTime;
      if (first) first.checked = true;
    }
    // Nur ein möglicher Weg (ein Plan, kein „Einmalig“) → Auswahl ausblenden, Plan steht unter dem Preis
    const choices = fieldset.querySelectorAll('[data-selling-plan]:not(:disabled)').length;
    fieldset.hidden = choices < 2;
  }

  get sellingPlanAllocation() {
    const planId = this.querySelector('[data-selling-plan]:checked:not(:disabled)')?.value;
    if (!planId) return null;
    return (this.variant?.selling_plan_allocations || []).find((allocation) => String(allocation.selling_plan_id) === planId) || null;
  }

  updatePrice() {
    if (!this.variant) return;
    const allocation = this.sellingPlanAllocation;
    const price = allocation ? allocation.price : this.variant.price;
    const priceEl = this.querySelector('[data-price] .mery-price');
    if (priceEl) priceEl.textContent = money(price);
    const sticky = this.querySelector('[data-sticky-price]');
    if (sticky) sticky.textContent = money(price);
    const note = this.querySelector('[data-plan-note]');
    if (note) {
      const name = this.querySelector('[data-selling-plan]:checked:not(:disabled)')?.dataset.planName;
      note.textContent = allocation && name ? `${window.MeryStrings.subscription || ''} ${name}`.trim() : '';
      note.hidden = !note.textContent;
    }
  }

  isPersonal(formatValue = '') {
    const value = formatValue.toLowerCase();
    return this.personalKeywords.some((keyword) => value.includes(keyword));
  }

  onOptionChange(updateUrl = true) {
    const options = this.selectedOptions;
    const variant = this.findVariant(options);
    this.variant = variant;

    const idInput = this.querySelector('[data-variant-id]');
    const submit = this.querySelector('[data-submit]');
    const label = this.querySelector('[data-submit-label]');
    if (variant && idInput) idInput.value = variant.id;

    const available = Boolean(variant?.available);
    if (submit) submit.disabled = !available;
    if (label) label.textContent = available ? label.dataset.labelAvailable || window.MeryStrings.addToCart : window.MeryStrings.soldOut;

    this.updatePurchase();
    if (variant) {
      this.updatePrice();
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
      if (variant.featured_media) this.showMedia(variant.featured_media.id);
    }

    const colorInput = this.querySelector('.mery-swatch-picker__input:checked');
    const colorLabel = this.querySelector('[data-selected-color]');
    if (colorInput && colorLabel) colorLabel.textContent = colorInput.value;

    const formatInput = this.querySelector('.mery-format-cards__input:checked');
    const specFormat = this.querySelector('[data-spec-format]');
    if (formatInput && specFormat) specFormat.textContent = formatInput.value;

    const personal = this.hasAttribute('data-personal-always') || this.isPersonal(formatInput?.value || '');
    const fieldset = this.querySelector('[data-personalization]');
    if (fieldset) {
      fieldset.hidden = !personal;
      fieldset.disabled = !personal;
      fieldset.querySelectorAll('[data-personal-required]').forEach((input) => (input.required = personal));
      this.querySelectorAll('[data-personal-only]').forEach((el) => (el.hidden = !personal));
    }
  }

  /* ---------- Warenkorb ---------- */

  async onSubmit(event) {
    event.preventDefault();
    const status = this.querySelector('[data-status]');
    const submit = this.querySelector('[data-submit]');
    status.textContent = '';

    const photo = this.form.querySelector('input[type="file"]');
    if (photo?.files?.[0] && photo.files[0].size > Number(photo.dataset.maxSize || 20) * 1024 * 1024) {
      photo.setCustomValidity(window.MeryStrings.photoTooLarge);
    } else {
      photo?.setCustomValidity('');
    }

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      const firstInvalid = this.form.querySelector(':invalid');
      firstInvalid?.focus();
      return;
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .join(',');

    const quantity = Math.max(1, Number(this.form.querySelector('[data-quantity]')?.value) || 1);
    const lines = [{ merchandiseId: String(this.variant?.id), quantity }];
    const addons = this.collectAddons();
    addons.forEach((id) => lines.push({ merchandiseId: String(id), quantity: 1 }));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));

    try {
      const formData = new FormData(this.form);
      // Leere Angaben nicht als Zeile in Warenkorb/Bestellung übernehmen
      for (const [key, value] of Array.from(formData.entries())) {
        const empty = value instanceof File ? value.size === 0 : String(value).trim() === '';
        if (key.startsWith('properties[') && empty) formData.delete(key);
      }
      if (formData.get('selling_plan') === '') formData.delete('selling_plan');
      if (!addons.length && sectionIds) formData.append('sections', sectionIds);
      let response = await this.post(formData);

      if (addons.length) {
        const title = this.querySelector('.mery-product__title')?.textContent.trim() || '';
        const payload = {
          items: addons.map((id) => ({ id, quantity: 1, properties: { [window.MeryStrings.addonProperty]: title } })),
        };
        if (sectionIds) payload.sections = sectionIds;
        response = await this.post(JSON.stringify(payload), true);
      }

      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mery-product',
          sourceId: this.dataset.sectionId,
          itemCount: lines.length,
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      status.textContent = window.MeryStrings.added;
    } catch (error) {
      deferred.reject(error);
      status.textContent = error.message || window.MeryStrings.error;
    } finally {
      submit.disabled = !this.variant?.available;
      submit.removeAttribute('aria-busy');
    }
  }

  async post(body, json = false) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: json
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || window.MeryStrings.error);
    return data;
  }

  collectAddons() {
    const [format, color] = [
      this.querySelector('.mery-format-cards__input:checked')?.value,
      this.querySelector('.mery-swatch-picker__input:checked')?.value,
    ];
    const ids = [];
    this.querySelectorAll('[data-addon]:checked').forEach((checkbox) => {
      const script = this.querySelector(`[data-addon-json="${CSS.escape(checkbox.value)}"]`);
      const variants = JSON.parse(script?.textContent || '[]').filter((variant) => variant.available);
      const match =
        variants.find((v) => v.options.includes(format) && v.options.includes(color)) ||
        variants.find((v) => v.options.includes(format)) ||
        variants.find((v) => v.options.includes(color)) ||
        variants[0];
      if (match) ids.push(match.id);
    });
    return ids;
  }

  /* ---------- Galerie ---------- */

  initGallery() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.counter = this.querySelector('[data-gallery-current]');
    this.dialog = this.querySelector('[data-zoom-dialog]');
    if (!this.track) return;

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)))
    );

    let ticking = false;
    this.track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setActive(index);
        });
      },
      { passive: true }
    );

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
    });

    this.querySelectorAll('[data-zoom-open]').forEach((button) =>
      button.addEventListener('click', () => this.openZoom(Number(button.dataset.zoomOpen)))
    );
    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.zoomTo(this.zoomIndex - 1));
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.zoomTo(this.zoomIndex + 1));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.zoomTo(this.zoomIndex + 1);
      if (event.key === 'ArrowLeft') this.zoomTo(this.zoomIndex - 1);
    });
    this.activeIndex = 0;
    this.initGalleryVideos();
  }

  /* Shopify-Videos in der Galerie: stumm, laufen nur sichtbar; bei „Bewegung reduzieren“ mit Steuerung statt Autoplay */
  initGalleryVideos() {
    const videos = Array.from(this.querySelectorAll('[data-gallery-video] video'));
    if (!videos.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      videos.forEach((video) => (video.controls = true));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) video.play().catch(() => (video.controls = true));
          else video.pause();
        }),
      { threshold: 0.6 }
    );
    videos.forEach((video) => observer.observe(video));
  }

  get slides() {
    return Array.from(this.track?.children || []);
  }

  goTo(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const target = Math.max(0, Math.min(slides.length - 1, index));
    this.track.scrollTo({ left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    this.setActive(target);
  }

  setActive(index) {
    this.activeIndex = index;
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = String(index + 1);
  }

  showMedia(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index > -1) this.goTo(index);
  }

  openZoom(index) {
    if (!this.dialog) return;
    const stage = this.dialog.querySelector('[data-zoom-stage]');
    if (!stage.children.length) {
      stage.append(this.dialog.querySelector('[data-zoom-images]').content.cloneNode(true));
    }
    this.dialog.showModal();
    this.zoomTo(index);
  }

  zoomTo(index) {
    const figures = Array.from(this.dialog.querySelectorAll('.mery-zoom__figure'));
    if (!figures.length) return;
    this.zoomIndex = (index + figures.length) % figures.length;
    figures.forEach((figure, i) => (figure.hidden = i !== this.zoomIndex));
  }

  /* ---------- Sticky-Leiste ---------- */

  initSticky() {
    const bar = this.querySelector('[data-sticky-buy]');
    const buy = this.querySelector('.mery-product__buy');
    if (!bar || !buy || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', passed);
        bar.setAttribute('aria-hidden', String(!passed));
        bar.querySelector('[data-sticky-submit]').tabIndex = passed ? 0 : -1;
      },
      { threshold: 0 }
    );
    observer.observe(buy);

    bar.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
      const personal = this.querySelector('[data-personalization]:not([hidden])');
      if (personal || !this.form.checkValidity()) {
        this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => this.form.reportValidity(), 400);
      } else {
        this.form.requestSubmit();
      }
    });
  }

  /* ---------- Zuletzt angesehen ---------- */

  rememberView() {
    try {
      const key = 'mery:recent';
      const handle = this.dataset.productHandle;
      const list = (JSON.parse(localStorage.getItem(key)) || []).filter((item) => item !== handle);
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (error) {
      /* Speicher nicht verfügbar */
    }
  }
}

if (!customElements.get('mery-product')) customElements.define('mery-product', MeryProduct);

/* Sanftes Einblenden der Abschnitte unter der Kaufbox – nur was beim Laden noch nicht sichtbar ist, nicht bei „Bewegung reduzieren“ */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
  const targets = Array.from(document.querySelectorAll('main .mery-section')).filter(
    (el) => el.getBoundingClientRect().top > window.innerHeight
  );
  if (!targets.length) return;
  const observer = new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }),
    { rootMargin: '0px 0px -10% 0px' }
  );
  targets.forEach((el) => {
    el.classList.add('mery-reveal');
    observer.observe(el);
  });
})();
